import { lazy, Suspense, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BrandMark, BrandWordmark } from '@/components/brand/BrandMark';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, Input, PasswordInput } from '@/components/ui/input';
import { useSessionState } from '@/features/session/use-session';

const formSchema = z.object({
  email: z.string().trim().min(1, 'Informe o e-mail.').email('E-mail inválido.'),
  password: z.string().min(1, 'Informe a senha.'),
});
type FormValues = z.infer<typeof formSchema>;

const LoginShowcase = lazy(() =>
  import('@/features/session/LoginShowcase').then((m) => ({ default: m.LoginShowcase })),
);

const FUNDO_DO_PAINEL =
  'linear-gradient(155deg, oklch(32% 0.08 255) 0%, oklch(24% 0.04 258) 55%, oklch(19% 0.02 260) 100%)';

/**
 * Entrada no sistema. A recusa é sempre a mesma — e-mail inexistente, senha errada, perfil
 * sem senha ou desativado devolvem "E-mail ou senha incorretos" —, porque a tela é pública
 * e não pode virar um descobridor de quem trabalha na empresa.
 */
export function LoginPage() {
  const { signIn } = useSessionState();

  const [erro, setErro] = useState<string | undefined>(undefined);
  const [entrando, setEntrando] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
  });

  // O título da aba enquanto ninguém entrou; a casca da aplicação põe o da tela depois.
  useEffect(() => {
    document.title = 'Entrar · ObraPlan';
  }, []);

  const entrar = async (values: FormValues) => {
    setEntrando(true);
    setErro(undefined);
    const falha = await signIn(values.email, values.password);
    setEntrando(false);
    if (falha !== undefined) {
      setErro(falha);
      form.setValue('password', '');
    }
  };

  return (
    <div className="grid min-h-full split:grid-cols-[1.15fr_0.85fr]">
      <aside
        className="relative hidden overflow-hidden split:block"
        style={{ background: FUNDO_DO_PAINEL }}
      >
        <Suspense fallback={null}>
          <LoginShowcase />
        </Suspense>
      </aside>

      <div className="entrada-suave flex items-center justify-center bg-(--color-surface-muted) px-5 py-6 split:p-8">
        <Card className="w-full max-w-[380px] rounded-2xl px-6 py-7 shadow-sm split:max-w-[360px]">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit(entrar)(event);
            }}
            className="flex flex-col gap-4"
          >
            <header className="mb-1.5 flex flex-col items-center text-center split:items-start split:text-left">
              <p className="text-[11px] font-bold tracking-[0.16em] text-(--color-brand) uppercase">
                F2R
              </p>
              {/* O logotipo inteiro — símbolo e nome — é o título da tela. */}
              <h1 className="mt-2 flex items-center gap-2.5">
                <BrandMark className="size-9" />
                <BrandWordmark className="text-2xl" />
              </h1>
              <p className="mt-1.5 text-sm text-(--color-content-muted)">
                Planejamento e execução de obras
              </p>
            </header>

            {erro !== undefined && (
              <p
                role="alert"
                className="rounded-md border border-(--color-state-danger) px-3 py-2 text-sm text-(--color-state-danger)"
              >
                {erro}
              </p>
            )}

            <Field
              label="E-mail"
              htmlFor="login-email"
              required
              error={form.formState.errors.email?.message}
            >
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                autoFocus
                aria-invalid={form.formState.errors.email !== undefined}
                {...form.register('email')}
              />
            </Field>

            <Field
              label="Senha"
              htmlFor="login-password"
              required
              error={form.formState.errors.password?.message}
            >
              <PasswordInput
                id="login-password"
                autoComplete="current-password"
                aria-invalid={form.formState.errors.password !== undefined}
                {...form.register('password')}
              />
            </Field>

            <Button type="submit" variant="primary" className="w-full" disabled={entrando}>
              {entrando ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
